import { useState } from 'react';
import type { ConnectionDetail, Me } from '@debtbot/shared';
import { useCreateTransaction } from '../api/hooks';
import { ApiError } from '../api/errors';
import { AmountInput, digitsToMinor } from '../components/AmountInput';
import { Button } from '../components/Button';
import { ErrorBanner } from '../components/ErrorBanner';
import { Sheet } from '../components/Sheet';
import { useT } from '../i18n';
import { formatMoney, fullName, primaryBalance } from '../lib/format';
import { haptic } from '../telegram/haptic';

/**
 * Погашение (PROJECT_SPEC §6.6). Направление вычисляется из знака баланса:
 * balance > 0 — peer должен мне, значит вернул peer; иначе вернул я.
 */
export function SettleSheet({
  open,
  onClose,
  connection,
  me,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  connection: ConnectionDetail;
  me: Me;
  onDone: () => void;
}) {
  const t = useT();
  const balance = primaryBalance(connection.balances);
  const abs = Math.abs(balance.amount);
  const payerId = balance.amount > 0 ? connection.peer.id : me.id;
  const peerName = fullName(connection.peer);
  const [mode, setMode] = useState<'full' | 'other'>('full');
  const [digits, setDigits] = useState('');
  const [changed, setChanged] = useState<string | null>(null);
  const create = useCreateTransaction(connection.id, me.id);

  const amount = mode === 'full' ? abs : digitsToMinor(digits, balance.currency);
  const overpay = mode === 'other' && amount > abs;

  const submit = async () => {
    if (amount <= 0) return;
    haptic.tap();
    setChanged(null);
    try {
      await create.mutateAsync({
        client_request_id: crypto.randomUUID(),
        type: 'SETTLEMENT',
        payer_id: payerId,
        amount,
        currency: balance.currency,
        category: null,
        note: null,
        expected_balance: mode === 'full' ? balance : null,
      });
      haptic.success();
      onDone();
    } catch (err) {
      haptic.error();
      if (err instanceof ApiError && err.code === 'BALANCE_CHANGED') {
        const actual =
          (err.details?.actual as { amount: number; currency: string } | undefined) ?? balance;
        setChanged(formatMoney({ ...actual, amount: Math.abs(actual.amount) }));
      }
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title={t.settle.title}>
      <div className="flex flex-col gap-3">
        <div className="text-hint">
          {balance.amount > 0 ? t.person.owesYou(peerName) : t.person.youOwe(peerName)}{' '}
          <b className="text-text">{formatMoney({ ...balance, amount: abs })}</b>
        </div>
        {changed && (
          <div className="rounded-xl bg-secondary-bg px-3 py-2 text-sm text-warning">
            {t.settle.changed(changed)}
          </div>
        )}
        {create.isError && !changed && <ErrorBanner error={create.error} />}

        {mode === 'full' ? (
          <>
            <Button
              data-testid="settle-full"
              onClick={() => void submit()}
              disabled={create.isPending || abs === 0}
            >
              {t.settle.full(formatMoney({ ...balance, amount: abs }))}
            </Button>
            <Button variant="secondary" onClick={() => setMode('other')}>
              {t.settle.other}
            </Button>
          </>
        ) : (
          <>
            <AmountInput
              digits={digits}
              onChange={setDigits}
              warning={
                overpay ? t.settle.overpay(formatMoney({ ...balance, amount: amount - abs })) : null
              }
            />
            <Button
              data-testid="settle-confirm"
              onClick={() => void submit()}
              disabled={create.isPending || amount <= 0}
            >
              {t.settle.confirm(formatMoney({ ...balance, amount }))}
            </Button>
          </>
        )}
        <div className="text-center text-sm text-hint">
          {t.settle.direction(
            payerId === me.id ? t.settle.me : peerName,
            payerId === me.id ? peerName : t.settle.toMe,
          )}
        </div>
      </div>
    </Sheet>
  );
}
